"use client";

import React, { useState, useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import Link from "next/link";
import { useWorkspace } from "@/lib/context/WorkspaceContext";
import { getViewerTier } from "@/lib/permissions";
import { MemberSidebar, SECTION_TITLES, type MemberSection } from "./MemberSidebar";
import { MemberTopBar } from "./MemberTopBar";
import { StaffSidebar, type StaffSection } from "@/components/staff/StaffSidebar";
import { ProfileDropdown } from "@/app/staff/page";
import TopNav from "@/components/TopNav";
import InactiveMemberBanner from "@/components/InactiveMemberBanner";
interface DashboardShellProps {
    children: React.ReactNode;
    activeSection?: MemberSection;
    title?: string;
    maxWidth?: string;
    /** Render children edge-to-edge without the padded, max-width content wrapper. */
    fullBleed?: boolean;
    /**
     * For routes that should stay publicly viewable. When set and the visitor is
     * not authenticated, render the public TopNav + children instead of redirecting
     * to login. Authenticated visitors still get the dashboard shell.
     */
    publicFallback?: boolean;
}

const STAFF_ROLES = ["staff", "admin", "superadmin"];

function LoadingScreen({ message = "Loading…" }: { message?: string }) {
    return (
        <div className="fixed inset-0 bg-[#003d45] flex items-center justify-center z-50">
            <div className="flex flex-col items-center gap-4">
                <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                <p className="text-white/50 text-[10px] font-black uppercase tracking-widest">{message}</p>
            </div>
        </div>
    );
}

export default function DashboardShell({ children, activeSection = "gigs", title, maxWidth, fullBleed = false, publicFallback = false }: DashboardShellProps) {
    const { user, isLoading, logout } = useWorkspace();
    const router = useRouter();
    const pathname = usePathname();
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        if (isLoading) return;
        if (!user && !publicFallback) {
            router.replace(`/login?redirectTo=${encodeURIComponent(pathname)}`);
            return;
        }
    }, [isLoading, user, router, pathname, publicFallback]);

    if (isLoading) return <LoadingScreen />;
    // Anonymous visitor on a public-fallback route: show the public chrome, no sidebar.
    if (!user && publicFallback) {
        return (
            <>
                <TopNav />
                {children}
            </>
        );
    }
    if (!user) return <LoadingScreen message="Redirecting…" />;

    const isStaff = STAFF_ROLES.includes((user.role || "").toLowerCase());

    const handleSelectSection = (s: MemberSection) => {
        // If we are on a sub-page, navigate to the main dashboard with the section query
        if (pathname !== "/dashboard") {
            router.push(`/dashboard?section=${s}`);
        }
    };

    const displayTitle = title || SECTION_TITLES[activeSection] || "Dashboard";
    const contentMaxWidth = maxWidth || (activeSection === "directory" ? "max-w-[1700px]" : "max-w-5xl");

    if (isStaff) {
        return (
            <div className="fixed inset-0 flex bg-[#f0f2f4] z-40">
                {/* Staff Sidebar */}
                <StaffSidebar
                    active="activities"
                    onSelect={(s) => router.push(`/staff?section=${s}`)}
                    user={user}
                    onLogout={logout}
                    mobileOpen={mobileOpen}
                    onMobileClose={() => setMobileOpen(false)}
                />

                {/* Main content area */}
                <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                    {/* Top navbar */}
                    <header className="flex-shrink-0 bg-white border-b border-[#e2e8f0] shadow-sm">
                        {/* Navigation bar */}
                        <div className="flex items-center justify-between px-4 lg:px-6 h-14 gap-3">
                            <button
                                onClick={() => setMobileOpen(true)}
                                className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-[#f0f2f4] transition-colors flex-shrink-0"
                                aria-label="Open menu"
                            >
                                <span className="material-symbols-outlined text-[#003d45]">menu</span>
                            </button>
                            <h1 className="text-sm lg:text-base font-black text-[#003d45] uppercase tracking-wider truncate flex-1 lg:flex-none">
                                {displayTitle}
                            </h1>
                            <nav className="hidden sm:flex items-center gap-1">
                                <Link
                                    href="/staff?section=org_volunteers"
                                    className="px-4 py-2 text-sm font-semibold text-[#666] hover:text-[#003d45] rounded-lg hover:bg-[#f0f2f4] transition-colors"
                                >
                                    Volunteers
                                </Link>
                                <Link
                                    href="/staff?section=org_groups"
                                    className="px-4 py-2 text-sm font-semibold text-[#666] hover:text-[#003d45] rounded-lg hover:bg-[#f0f2f4] transition-colors"
                                >
                                    Groups
                                </Link>
                            </nav>
                            <ProfileDropdown user={user} onLogout={logout} />
                        </div>
                    </header>

                    {/* Scrollable panel area */}
                    <main className={`flex-1 min-h-0 overflow-y-auto ${fullBleed ? "" : "p-6 lg:p-8"}`}>
                        <div className={`mx-auto w-full ${fullBleed ? "" : contentMaxWidth}`}>
                            {children}
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    const tier = getViewerTier(user);
    const isLeader = tier === "leader" || tier === "admin";

    return (
        <div className="fixed inset-0 flex bg-[#f7f7f7] z-40">
            {/* Sidebar */}
            <MemberSidebar
                active={activeSection}
                onSelect={handleSelectSection}
                user={user}
                isLeader={isLeader}
                onLogout={logout}
                mobileOpen={mobileOpen}
                onMobileClose={() => setMobileOpen(false)}
            />

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                {/* Top bar */}
                <MemberTopBar
                    title={displayTitle}
                    user={user}
                    onLogout={logout}
                    onSelectSection={handleSelectSection}
                    onMobileMenuOpen={() => setMobileOpen(true)}
                />

                {activeSection !== "gigs" && <InactiveMemberBanner key={activeSection} />}

                {/* Scrollable panel */}
                <main className={`flex-1 min-h-0 overflow-y-auto ${fullBleed ? "" : "p-6 lg:p-8"}`}>
                    <div className={`mx-auto w-full ${fullBleed ? "" : contentMaxWidth}`}>
                        {children}
                    </div>
                </main>
            </div>
        </div>
    );
}
